import { useMemo } from "react";

import { getTicks, getXTicks, replaceExtraLargeValue } from "./helpers";
import type { Config, Data, FormattedData, XItem, YItem } from "./types";

export const useConfig = (data: Data, config: Config) => {
  const { xAxes, yAxes, lines } = useMemo(
    () => ({
      xAxes: config.filter(item => item.xAxis),
      yAxes: config.filter(item => item.yAxis),
      lines: config.filter(item => item.line),
    }),
    [config]
  );

  const formattedYAxes = useMemo(
    () =>
      yAxes.map(item => {
        const { from = -Infinity, to = Infinity } = item.yAxis?.rangeLimit ?? {};
        // @ts-ignore
        const filteredValues = data.map(obj => item.yAxis?.formatter?.(obj[item.dataKey]) ?? obj[item.dataKey]).filter(value => value > from && value < to);

        const min = Math.min(...filteredValues);
        const max = Math.max(...filteredValues);

        const ticks = getTicks(item.yAxis?.tickCount ?? 4, [min, max], {
          offsetInPercents: item.yAxis?.chartOffset,
          startFromZero: item.yAxis?.startFromZero,
        });

        return {
          ...item,
          ticks,
          domain: ticks ? ([ticks[0], ticks[ticks.length - 1]] as [number, number]) : undefined,
        };
      }),
    [data, yAxes]
  );

  const formattedXAxes = useMemo(
    () =>
      xAxes.map(item => {
        // @ts-ignore
        const filteredValues = data.map(obj => item.yAxis?.formatter?.(obj[item.dataKey]) ?? obj[item.dataKey]);

        const min = filteredValues[0];
        const max = filteredValues[filteredValues.length - 1];

        const { ticks, domain } = getXTicks([min, max]);

        return {
          ...item,
          ticks: ticks.map(date => date.getTime()),
          domain: domain.map(date => date.getTime()),
        };
      }),
    [xAxes, data]
  );

  return { xAxes: formattedXAxes, yAxes: formattedYAxes, lines };
};

export const useFormattedData = (data: Data, xAxes: XItem[], yAxes: YItem[]) => {
  return useMemo(
    () =>
      data.map(item => {
        const xData = xAxes.reduce<FormattedData>(
          (acc, axis) => ({
            ...acc,
            [axis.dataKey]: {
              // @ts-ignore
              value: axis.xAxis?.formatter ? axis.xAxis.formatter(item[axis.dataKey]) : item[axis.dataKey],
              // @ts-ignore
              origin: item[axis.dataKey],
              // @ts-ignore
              label: axis.xAxis?.tickFormatter ? axis.xAxis.tickFormatter(item[axis.dataKey]) : item[axis.dataKey].toString(),
            },
          }),
          {}
        );

        const yData = yAxes.reduce<FormattedData>(
          (acc, axis) => ({
            ...acc,
            [axis.dataKey]: {
              value: axis.yAxis?.formatter
                ? // @ts-ignore
                  axis.yAxis.formatter(replaceExtraLargeValue(item[axis.dataKey], axis.domain))
                : // @ts-ignore
                  replaceExtraLargeValue(item[axis.dataKey], axis.domain),
              // @ts-ignore
              origin: item[axis.dataKey],
              // @ts-ignore
              label: axis.yAxis?.tickFormatter ? axis.yAxis.tickFormatter(item[axis.dataKey]) : item[axis.dataKey].toString(),
            },
          }),
          {}
        );

        return {
          ...xData,
          ...yData,
        };
      }),
    [data, xAxes, yAxes]
  );
};
